import React, { useEffect, useRef } from "react";
import { Input } from "@chakra-ui/react";
import { useCustomTheme } from "../contexts/CustomThemeContext";

interface AutocompleteInputProps {
  placeholder?: string;
  value?: string;
  onPlaceSelected: (address: string) => void;
  onLocationSelected?: (coords: { lat: number; lng: number }) => void;
  /**
   * Optional callback used when the caller needs to distinguish normal typing
   * from a confirmed Google Places selection.
   *
   * Backwards compatibility:
   * If this is not supplied, typing continues to call onPlaceSelected exactly
   * like the previous component did, so existing usages do not need changing.
   */
  onInputChange?: (value: string) => void;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  placeholder,
  onPlaceSelected,
  onLocationSelected,
  onInputChange,
  value,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Keep the newest parent callbacks available to the Google listener without
  // rebuilding the Autocomplete instance every time the parent re-renders.
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const onLocationSelectedRef = useRef(onLocationSelected);
  const onInputChangeRef = useRef(onInputChange);

  const { theme } = useCustomTheme();

  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    onLocationSelectedRef.current = onLocationSelected;
  }, [onLocationSelected]);

  useEffect(() => {
    onInputChangeRef.current = onInputChange;
  }, [onInputChange]);

  useEffect(() => {
    // GoogleMapsProvider guarantees this before render in the existing app.
    if (
      !window.google?.maps?.places ||
      !inputRef.current ||
      autocompleteRef.current
    ) {
      return;
    }

    const input = inputRef.current;

    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: "mw" },
      fields: ["formatted_address", "geometry", "name", "place_id"],
    });

    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const location = place.geometry?.location;

      /*
       * Prefer Google's complete formatted address. If Google does not return
       * formatted_address for a particular prediction, the native input value
       * is the next-best representation because it contains the prediction the
       * user actually selected. `name` is only the final fallback.
       */
      const selectedAddress = (
        place.formatted_address ||
        input.value ||
        place.name ||
        ""
      ).trim();

      if (!selectedAddress) {
        return;
      }

      /*
       * Google writes the selected prediction into the native input. Updating
       * React state here with the exact selected address prevents the controlled
       * Chakra input from reverting to the search words the user typed.
       */
      onPlaceSelectedRef.current(selectedAddress);

      if (location && onLocationSelectedRef.current) {
        onLocationSelectedRef.current({
          lat: location.lat(),
          lng: location.lng(),
        });
      }
    });

    return () => {
      window.google?.maps?.event?.removeListener(listener);
      autocompleteRef.current = null;
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    /*
     * New screens can separate typing from a confirmed place by supplying
     * onInputChange. Existing screens that do not supply it keep the old API:
     * typing still flows through onPlaceSelected.
     */
    if (onInputChangeRef.current) {
      onInputChangeRef.current(nextValue);
      return;
    }

    onPlaceSelectedRef.current(nextValue);
  };

  return (
    <Input
      ref={inputRef}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      autoComplete="off"
      bg={theme.inputBg}
      border="1px solid"
      borderColor={theme.inputBorder}
      fontWeight="600"
      color={theme.text}
      _placeholder={{ color: theme.inputPlaceholder }}
      _hover={{ borderColor: theme.inputBorderFocus }}
      _focus={{
        borderColor: theme.inputBorderFocus,
        boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
      }}
      borderRadius="md"
    />
  );
};

export default AutocompleteInput;
